import type {
  TransitionV2,
  V2ComparisonOperator,
  V2MatchMode,
  V2OrganisationConditionFieldName,
  V2OrganisationConditions,
  V2PayloadCondition
} from '../../../ProgramStateMachine.types'
import { V2_ORGANISATION_FIELDS } from '../../../utils/stateMachineV2'

import { fromConditionFormValue, joinValues, parseValuesText, toConditionFormValue } from './transitionV2FormHelpers'

export type OrgFieldValues = { enabled: boolean; valuesText: string; isOneOf: boolean }

export type OrgFieldsFormValue = Record<V2OrganisationConditionFieldName, OrgFieldValues>

export interface V2ConditionFormValue {
  type: 'set' | 'numeric'
  key: string
  valuesText: string
  isOneOf: boolean
  numericValue: string
  numericOperator: V2ComparisonOperator
}

export interface V2DataEventFormValue {
  eventType: string
  matchMode: V2MatchMode
  payload: V2ConditionFormValue[]
}

export interface V2FormValues {
  targetState: string
  timeGreaterThan: number | null
  topLogicalOperator: 'and' | 'or'
  topEvents: { type: string }[]
  orgConditions: OrgFieldsFormValue
  dataLogicalOperator: 'and' | 'or'
  dataEvents: V2DataEventFormValue[]
}

export const emptyOrgFields = (
  fields: readonly V2OrganisationConditionFieldName[] = V2_ORGANISATION_FIELDS
): OrgFieldsFormValue =>
  fields.reduce(
    (acc, f) => ({ ...acc, [f]: { enabled: false, valuesText: '', isOneOf: true } }),
    {} as OrgFieldsFormValue
  )

export const emptyCondition = (): V2ConditionFormValue => ({
  type: 'set',
  key: '',
  valuesText: '',
  isOneOf: true,
  numericValue: '',
  numericOperator: '=='
})

export const emptyDataEvent = (): V2DataEventFormValue => ({
  eventType: '',
  matchMode: 'any',
  payload: []
})

export const toV2FormValues = (
  transition?: TransitionV2,
  orgFields: readonly V2OrganisationConditionFieldName[] = V2_ORGANISATION_FIELDS
): V2FormValues => {
  if (!transition) {
    return {
      targetState: '',
      timeGreaterThan: null,
      topLogicalOperator: 'and',
      topEvents: [],
      orgConditions: emptyOrgFields(orgFields),
      dataLogicalOperator: 'and',
      dataEvents: []
    }
  }

  const w = transition.when
  const orgConditions = emptyOrgFields(orgFields)

  const org = w.data?.organisation

  if (org) {
    for (const f of orgFields) {
      const cond = org[f]

      if (cond) {
        orgConditions[f] = {
          enabled: true,
          valuesText: joinValues(cond.values),
          isOneOf: cond.isOneOf ?? true
        }
      }
    }
  }

  const dataEvents: V2DataEventFormValue[] = (w.data?.eventsMatching?.events ?? []).map(ev => ({
    eventType: ev.eventType,
    matchMode: ev.matchMode ?? 'any',
    payload: ev.payload.map(toConditionFormValue)
  }))

  return {
    targetState: transition.to,
    timeGreaterThan: w.timeGreaterThan !== undefined ? w.timeGreaterThan * 1000 : null,
    topLogicalOperator: w.eventsMatching?.logicalOperator ?? 'and',
    topEvents: (w.eventsMatching?.events ?? []).map(e => ({ type: e.type })),
    orgConditions,
    dataLogicalOperator: w.data?.eventsMatching?.logicalOperator ?? 'and',
    dataEvents
  }
}

export const fromV2FormValues = (
  values: V2FormValues,
  orgFields: readonly V2OrganisationConditionFieldName[] = V2_ORGANISATION_FIELDS
): TransitionV2 => {
  const when: TransitionV2['when'] = {}

  if (values.timeGreaterThan !== null) {
    when.timeGreaterThan = Math.floor(values.timeGreaterThan / 1000)
  }

  const topEvents = values.topEvents.filter(e => e.type.trim() !== '')

  if (topEvents.length > 0) {
    when.eventsMatching = {
      logicalOperator: values.topLogicalOperator,
      events: topEvents.map(e => ({ type: e.type }))
    }
  }

  const organisation: V2OrganisationConditions = {}
  let hasOrgField = false

  for (const f of orgFields) {
    const entry = values.orgConditions[f]

    if (entry.enabled) {
      const parsed = parseValuesText(entry.valuesText).map(v => String(v))

      if (parsed.length > 0) {
        organisation[f] = { values: parsed, isOneOf: entry.isOneOf }
        hasOrgField = true
      }
    }
  }

  const dataEvents = values.dataEvents
    .filter(ev => ev.eventType.trim() !== '')
    .map(ev => ({
      eventType: ev.eventType,
      matchMode: ev.matchMode,
      payload: ev.payload.map(fromConditionFormValue).filter((c): c is V2PayloadCondition => c !== null)
    }))

  const hasDataEvents = dataEvents.length > 0

  if (hasOrgField || hasDataEvents) {
    when.data = {}

    if (hasOrgField) when.data.organisation = organisation

    if (hasDataEvents) {
      when.data.eventsMatching = {
        logicalOperator: values.dataLogicalOperator,
        events: dataEvents
      }
    }
  }

  return { to: values.targetState, when }
}
